import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Send, Trash2, Users, CalendarDays } from 'lucide-react';

const WEEKDAYS_SHORT = ['Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab', 'Dom'];
const WEEKDAYS_FULL = ['Lunes', 'Martes', 'Miercoles', 'Jueves', 'Viernes', 'Sabado', 'Domingo'];

const USER_COLORS = [
  { bg: 'bg-amber-500/15', border: 'border-amber-500/25', text: 'text-amber-400', dot: 'bg-amber-500' },
  { bg: 'bg-blue-500/15', border: 'border-blue-500/25', text: 'text-blue-400', dot: 'bg-blue-500' },
  { bg: 'bg-emerald-500/15', border: 'border-emerald-500/25', text: 'text-emerald-400', dot: 'bg-emerald-500' },
  { bg: 'bg-purple-500/15', border: 'border-purple-500/25', text: 'text-purple-400', dot: 'bg-purple-500' },
  { bg: 'bg-pink-500/15', border: 'border-pink-500/25', text: 'text-pink-400', dot: 'bg-pink-500' },
  { bg: 'bg-cyan-500/15', border: 'border-cyan-500/25', text: 'text-cyan-400', dot: 'bg-cyan-500' },
  { bg: 'bg-orange-500/15', border: 'border-orange-500/25', text: 'text-orange-400', dot: 'bg-orange-500' },
  { bg: 'bg-rose-500/15', border: 'border-rose-500/25', text: 'text-rose-400', dot: 'bg-rose-500' },
];

function getWeekDays(baseDate) {
  const d = new Date(baseDate);
  const day = d.getDay();
  const mon = new Date(d);
  mon.setDate(d.getDate() - ((day + 6) % 7));
  return Array.from({ length: 7 }, (_, i) => {
    const dd = new Date(mon);
    dd.setDate(mon.getDate() + i);
    return dd.toISOString().split('T')[0];
  });
}

function getMonthDays(baseDate) {
  const d = new Date(baseDate);
  const year = d.getFullYear();
  const month = d.getMonth();
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startPad = (first.getDay() + 6) % 7;
  const days = [];
  for (let i = startPad - 1; i >= 0; i--) {
    const dd = new Date(first);
    dd.setDate(dd.getDate() - i - 1);
    days.push({ date: dd.toISOString().split('T')[0], inMonth: false });
  }
  for (let i = 1; i <= last.getDate(); i++) {
    days.push({ date: new Date(year, month, i).toISOString().split('T')[0], inMonth: true });
  }
  const remaining = 7 - (days.length % 7);
  if (remaining < 7) {
    for (let i = 1; i <= remaining; i++) {
      const dd = new Date(last);
      dd.setDate(dd.getDate() + i);
      days.push({ date: dd.toISOString().split('T')[0], inMonth: false });
    }
  }
  return days;
}

function formatShort(d) { return new Date(d + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }); }
function formatMonthYear(d) { return new Date(d).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }); }

export default function Schedule() {
  const { user, hasPermission } = useAuth();
  const canEdit = hasPermission('schedule.create');
  const [baseDate, setBaseDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');
  const [shifts, setShifts] = useState([]);
  const [users, setUsers] = useState([]);
  const [filterUser, setFilterUser] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ user_id: '', date: '', start_time: '09:00', end_time: '17:00', notes: '' });

  const weekDays = useMemo(() => getWeekDays(baseDate), [baseDate]);
  const monthDays = useMemo(() => getMonthDays(baseDate), [baseDate]);

  const dateFrom = viewMode === 'week' ? weekDays[0] : monthDays[0].date;
  const dateTo = viewMode === 'week' ? weekDays[6] : monthDays[monthDays.length - 1].date;

  const load = useCallback(async () => {
    try {
      const [s, u] = await Promise.all([
        API.get(`/shifts?date_from=${dateFrom}&date_to=${dateTo}`),
        API.get('/users/names')
      ]);
      setShifts(s.data);
      setUsers(u.data);
    } catch { toast.error('Error cargando cuadrantes'); }
  }, [dateFrom, dateTo]);

  useEffect(() => { load(); }, [load]);

  const navigate = (dir) => {
    const d = new Date(baseDate);
    if (viewMode === 'week') d.setDate(d.getDate() + (dir * 7));
    else d.setMonth(d.getMonth() + dir);
    setBaseDate(d);
  };

  const goToday = () => setBaseDate(new Date());

  const userColorMap = useMemo(() => {
    const map = {};
    users.forEach((u, i) => { map[u.id] = USER_COLORS[i % USER_COLORS.length]; });
    return map;
  }, [users]);

  const filteredShifts = useMemo(() => {
    if (filterUser === 'all') return shifts;
    return shifts.filter(s => s.user_id === filterUser);
  }, [shifts, filterUser]);

  const umap = Object.fromEntries(users.map(u => [u.id, u.name]));
  const draftCount = shifts.filter(s => s.status === 'draft').length;

  const openCreate = (date) => {
    setEditing(null);
    setForm({ user_id: users[0]?.id || '', date: date || weekDays[0], start_time: '09:00', end_time: '17:00', notes: '' });
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ user_id: s.user_id, date: s.date, start_time: s.start_time, end_time: s.end_time, notes: s.notes || '' });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await API.put(`/shifts/${editing.id}`, form);
        toast.success('Turno actualizado');
      } else {
        await API.post('/shifts', form);
        toast.success('Turno creado');
      }
      setDialogOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (sid, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Eliminar turno?')) return;
    try { await API.delete(`/shifts/${sid}`); toast.success('Turno eliminado'); load(); }
    catch { toast.error('Error'); }
  };

  const handlePublish = async () => {
    try {
      const res = await API.post(`/shifts/publish?date_from=${dateFrom}&date_to=${dateTo}`);
      toast.success(`${res.data.published_count} turnos publicados`);
      load();
    } catch { toast.error('Error publicando'); }
  };

  const today = new Date().toISOString().split('T')[0];

  const headerLabel = viewMode === 'week'
    ? `${formatShort(weekDays[0])} - ${formatShort(weekDays[6])}`
    : formatMonthYear(baseDate);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="schedule-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold text-zinc-50">Cuadrantes</h1>
          <p className="text-sm text-zinc-500">Gestion de turnos y horarios</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canEdit && draftCount > 0 && (
            <Button onClick={handlePublish} className="bg-emerald-500 hover:bg-emerald-600 text-black font-medium h-9" data-testid="publish-shifts-button">
              <Send className="w-4 h-4 mr-2" /> Publicar ({draftCount})
            </Button>
          )}
          {canEdit && (
            <Button onClick={() => openCreate(null)} className="bg-amber-500 hover:bg-amber-600 text-black font-medium h-9" data-testid="create-shift-button">
              <Plus className="w-4 h-4 mr-2" /> Nuevo Turno
            </Button>
          )}
        </div>
      </div>

      {/* Controls bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 rounded-xl bg-zinc-900/40 border border-white/5">
        {/* View toggle */}
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="bg-zinc-800/60 h-8">
            <TabsTrigger value="week" className="text-xs h-7 px-3" data-testid="view-week-tab">
              <CalendarDays className="w-3.5 h-3.5 mr-1.5" />Semana
            </TabsTrigger>
            <TabsTrigger value="month" className="text-xs h-7 px-3" data-testid="view-month-tab">
              <CalendarDays className="w-3.5 h-3.5 mr-1.5" />Mes
            </TabsTrigger>
          </TabsList>
        </Tabs>

        {/* User filter */}
        <Select value={filterUser} onValueChange={setFilterUser}>
          <SelectTrigger className="bg-zinc-950/50 border-white/10 w-44 h-8 text-xs" data-testid="filter-user-select">
            <Users className="w-3.5 h-3.5 mr-1.5 text-zinc-500" />
            <SelectValue placeholder="Todos" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-white/10">
            <SelectItem value="all">Todos los empleados</SelectItem>
            {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
          </SelectContent>
        </Select>

        {/* Date navigation */}
        <div className="flex items-center gap-1 ml-auto">
          <Button variant="ghost" size="sm" onClick={goToday} className="text-xs text-amber-500/70 hover:text-amber-500 h-7 px-2" data-testid="go-today-button">Hoy</Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(-1)} data-testid="prev-period-button"><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm font-medium text-zinc-300 min-w-[140px] text-center capitalize">{headerLabel}</span>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(1)} data-testid="next-period-button"><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* User legend */}
      {filterUser === 'all' && users.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {users.map(u => {
            const c = userColorMap[u.id];
            return (
              <button key={u.id} onClick={() => setFilterUser(u.id)}
                className="flex items-center gap-1.5 px-2 py-1 rounded-md text-xs transition-colors hover:bg-white/5"
                data-testid={`legend-user-${u.id}`}>
                <span className={`w-2 h-2 rounded-full ${c?.dot}`} />
                <span className="text-zinc-400">{u.name}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* WEEK VIEW */}
      {viewMode === 'week' && (
        <div className="grid grid-cols-1 md:grid-cols-7 gap-1.5">
          {weekDays.map((date, idx) => {
            const dayShifts = filteredShifts.filter(s => s.date === date);
            const isToday = date === today;
            const dayNum = date.split('-')[2];
            return (
              <div key={date}
                className={`rounded-xl border p-2.5 min-h-[160px] transition-colors ${isToday ? 'border-amber-500/30 bg-amber-500/5' : 'border-white/[0.04] bg-zinc-900/20 hover:bg-zinc-900/40'}`}
                data-testid={`schedule-day-${date}`}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-1.5">
                    <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-semibold ${isToday ? 'bg-amber-500 text-black' : 'text-zinc-500'}`}>
                      {dayNum}
                    </span>
                    <span className={`text-xs font-medium ${isToday ? 'text-amber-500' : 'text-zinc-600'}`}>{WEEKDAYS_SHORT[idx]}</span>
                  </div>
                  <Button variant="ghost" size="icon" className="h-6 w-6 opacity-50 hover:opacity-100" onClick={() => canEdit && openCreate(date)} style={{ visibility: canEdit ? 'visible' : 'hidden' }}>
                    <Plus className="w-3 h-3" />
                  </Button>
                </div>
                <div className="space-y-1">
                  {dayShifts.map(s => {
                    const c = userColorMap[s.user_id] || USER_COLORS[0];
                    return (
                      <div key={s.id}
                        className={`p-2 rounded-lg text-xs group transition-all ${canEdit ? 'cursor-pointer hover:scale-[1.02]' : ''} ${s.status === 'draft' ? `${c.bg} border ${c.border} border-dashed opacity-70` : `${c.bg} border ${c.border}`}`}
                        onClick={() => canEdit && openEdit(s)} data-testid={`shift-card-${s.id}`}>
                        <div className="flex justify-between items-center">
                          <span className={`font-semibold truncate ${c.text}`}>{umap[s.user_id] || '?'}</span>
                          {canEdit && (
                            <Button variant="ghost" size="icon" className="h-4 w-4 opacity-0 group-hover:opacity-100"
                              onClick={e => handleDelete(s.id, e)}>
                              <Trash2 className="w-2.5 h-2.5 text-red-400" />
                            </Button>
                          )}
                        </div>
                        <div className="text-zinc-400 mt-0.5 font-medium">{s.start_time} - {s.end_time}</div>
                        {s.status === 'draft' && <span className="text-[9px] text-amber-500/50 uppercase tracking-wider">borrador</span>}
                      </div>
                    );
                  })}
                  {dayShifts.length === 0 && <p className="text-[10px] text-zinc-700 text-center py-6">-</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* MONTH VIEW */}
      {viewMode === 'month' && (
        <div>
          <div className="grid grid-cols-7 gap-px mb-px">
            {WEEKDAYS_SHORT.map(d => (
              <div key={d} className="text-center text-[10px] text-zinc-600 font-medium uppercase tracking-wider py-2">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7 gap-px bg-white/[0.02] rounded-xl overflow-hidden border border-white/[0.04]">
            {monthDays.map(({ date, inMonth }) => {
              const dayShifts = filteredShifts.filter(s => s.date === date);
              const isToday = date === today;
              const dayNum = parseInt(date.split('-')[2]);
              return (
                <div key={date}
                  className={`min-h-[90px] p-1.5 transition-colors ${inMonth ? 'bg-zinc-950/60' : 'bg-zinc-950/30'} ${isToday ? 'ring-1 ring-inset ring-amber-500/30' : ''}`}
                  data-testid={`month-day-${date}`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-[11px] font-medium w-5 h-5 flex items-center justify-center rounded ${isToday ? 'bg-amber-500 text-black' : inMonth ? 'text-zinc-400' : 'text-zinc-700'}`}>
                      {dayNum}
                    </span>
                    {inMonth && canEdit && (
                      <button onClick={() => openCreate(date)} className="opacity-0 hover:opacity-100 focus:opacity-100 transition-opacity">
                        <Plus className="w-2.5 h-2.5 text-zinc-600" />
                      </button>
                    )}
                  </div>
                  <div className="space-y-0.5">
                    {dayShifts.slice(0, 3).map(s => {
                      const c = userColorMap[s.user_id] || USER_COLORS[0];
                      return (
                        <div key={s.id}
                          className={`px-1 py-0.5 rounded text-[9px] truncate ${canEdit ? 'cursor-pointer' : ''} ${c.bg} ${c.text} ${s.status === 'draft' ? 'opacity-50' : ''}`}
                          onClick={() => canEdit && openEdit(s)}
                          title={`${umap[s.user_id]} ${s.start_time}-${s.end_time}`}
                          data-testid={`shift-month-${s.id}`}>
                          <span className="font-medium">{umap[s.user_id]?.split(' ')[0]}</span>
                          <span className="text-zinc-500 ml-1">{s.start_time}</span>
                        </div>
                      );
                    })}
                    {dayShifts.length > 3 && (
                      <span className="text-[9px] text-zinc-600 px-1">+{dayShifts.length - 3} mas</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Shift dialog - only for editors */}
      {canEdit && (
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-950 border-white/10 sm:max-w-md" data-testid="shift-dialog">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 font-heading">{editing ? 'Editar Turno' : 'Nuevo Turno'}</DialogTitle>
            <DialogDescription className="text-zinc-500">Configura los datos del turno</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-zinc-300">Empleado</Label>
              <Select value={form.user_id} onValueChange={v => setForm(f => ({ ...f, user_id: v }))}>
                <SelectTrigger className="bg-zinc-950/50 border-white/10" data-testid="shift-user-select"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                <SelectContent className="bg-zinc-950 border-white/10">
                  {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                      <div className="flex items-center gap-2">
                        <span className={`w-2 h-2 rounded-full ${userColorMap[u.id]?.dot || 'bg-zinc-500'}`} />
                        {u.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Fecha</Label>
              <Input type="date" value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" data-testid="shift-date-input" aria-label="Fecha" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label className="text-zinc-300">Inicio</Label>
                <Input type="time" value={form.start_time} onChange={e => setForm(f => ({ ...f, start_time: e.target.value }))}
                  className="bg-zinc-950/50 border-white/10" data-testid="shift-start-input" aria-label="Hora inicio" />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Fin</Label>
                <Input type="time" value={form.end_time} onChange={e => setForm(f => ({ ...f, end_time: e.target.value }))}
                  className="bg-zinc-950/50 border-white/10" data-testid="shift-end-input" aria-label="Hora fin" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-zinc-300">Notas</Label>
              <Input value={form.notes || ''} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="bg-zinc-950/50 border-white/10" placeholder="Opcional" aria-label="Notas" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setDialogOpen(false)} className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200">Cancelar</Button>
            <Button onClick={handleSave} className="bg-amber-500 hover:bg-amber-600 text-black font-medium" data-testid="shift-save-button">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}
    </div>
  );
}
